<template>
    <div>
        <template v-if="!error">
            <ContentOrLoading style="min-height: 500px;">
                <b-row v-if="reserva && !butacasEnSesion && !agotado && !comprado">
                    <b-col cols="12">
                        <PageTitle title=""/>
                        <CabeceraClubs
                            v-if="reserva.evento.equipo_local"
                            :event="reserva.evento"
                        />
                        <CabeceraImagen
                            v-if="!reserva.evento.equipo_local"
                            :event="reserva.evento"
                            :sesion="reserva.sesion"
                        />
    
                        <Stepper
                            ref="stepper"
                            :steps="steps" 
                        >
                            <template #seleccionCantidad="{ changeStep }">
                                <div class="container">
                                    <div class="row justify-content-center">
                                        <div class="col-12 col-md-8 col-lg-6 pb-5">
                                            <h2 class="m-0 h1 text-uppercase">
                                                {{ $t('reservas.seleccionar_cantidad') }}
                                            </h2>

                                            <!-- Modo multi-precio: un contador por tipo de precio -->
                                            <template v-if="esMultiPrecio">
                                                <div class="subtitle h3 light mb-3 mb-lg-5">
                                                    {{ $t('reservas.selecciona_entradas') }}
                                                </div>

                                                <div
                                                    v-for="precio in reserva.precios"
                                                    :key="precio.id"
                                                    class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border tw:border-gray-200 tw:rounded-xl tw:px-4 tw:py-3 tw:mb-3"
                                                >
                                                    <div class="tw:flex-1">
                                                        <div class="tw:font-bold tw:text-base oswald tw:uppercase">
                                                            {{ precio.nombre }}
                                                        </div>
                                                        <div class="tw:text-sm tw:text-gray-500">
                                                            {{ precio.precio | formatear_precio }}
                                                            &nbsp;&middot;&nbsp;
                                                            {{ $t('reservas.disponibles') }}: <strong>{{ precio.available_units_for_this_price }}</strong>
                                                        </div>
                                                    </div>
                                                    <div class="tw:flex tw:items-center tw:gap-2 tw:shrink-0">
                                                        <button
                                                            type="button"
                                                            class="tw:inline-flex tw:items-center tw:justify-center tw:shrink-0 tw:w-7 tw:h-7 tw:p-0 tw:m-0 tw:border-2 tw:border-current tw:rounded-full tw:bg-transparent tw:leading-none tw:cursor-pointer tw:select-none tw:transition-colors tw:duration-200 disabled:tw:opacity-35 disabled:tw:cursor-not-allowed hover:enabled:tw:bg-current hover:enabled:[&>span]:tw:invert"
                                                            :disabled="getCantidadPorPrecio(precio.id) <= 0"
                                                            @click="decrementarCantidad(precio.id)"
                                                            :aria-label="$t('reservas.restar')"
                                                        ><span class="tw:block tw:leading-none tw:text-base tw:font-normal" aria-hidden="true">−</span></button>

                                                        <b-form-input
                                                            :value="getCantidadPorPrecio(precio.id)"
                                                            type="number"
                                                            :min="0"
                                                            :max="precio.available_units_for_this_price"
                                                            class="cantidad-input tw:w-12 tw:h-7 tw:text-center tw:text-base tw:font-bold tw:border-2 tw:border-gray-300 tw:rounded-lg tw:px-1 tw:py-0"
                                                            @change="val => setCantidadPorPrecio(precio.id, Number(val), precio.available_units_for_this_price)"
                                                        />

                                                        <button
                                                            type="button"
                                                            class="tw:inline-flex tw:items-center tw:justify-center tw:shrink-0 tw:w-7 tw:h-7 tw:p-0 tw:m-0 tw:border-2 tw:border-current tw:rounded-full tw:bg-transparent tw:leading-none tw:cursor-pointer tw:select-none tw:transition-colors tw:duration-200 disabled:tw:opacity-35 disabled:tw:cursor-not-allowed hover:enabled:tw:bg-current hover:enabled:[&>span]:tw:invert"
                                                            :disabled="getCantidadPorPrecio(precio.id) >= precio.available_units_for_this_price"
                                                            @click="incrementarCantidad(precio.id, precio.available_units_for_this_price)"
                                                            :aria-label="$t('reservas.sumar')"
                                                        ><span class="tw:block tw:leading-none tw:text-base tw:font-normal" aria-hidden="true">+</span></button>
                                                    </div>
                                                </div>
                                            </template>

                                            <!-- Modo genérico simple: un solo precio -->
                                            <template v-else>
                                                <div class="subtitle h3 light mb-3 mb-lg-5">
                                                    {{ $t('reservas.selecciona_entradas') }}
                                                </div>

                                                <div class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border tw:border-gray-200 tw:rounded-xl tw:px-4 tw:py-3 tw:mb-3">
                                                    <div class="tw:flex-1">
                                                        <div class="tw:font-bold tw:text-base oswald tw:uppercase">
                                                            {{ reserva.precios[0].nombre }}
                                                        </div>
                                                        <div class="tw:text-sm tw:text-gray-500">
                                                            {{ reserva.precios[0].precio | formatear_precio }}
                                                            &nbsp;&middot;&nbsp;
                                                            {{ $t('reservas.disponibles') }}: <strong>{{ reserva.precios[0].available_units_for_this_price }}</strong>
                                                        </div>
                                                    </div>
                                                    <div class="tw:flex tw:items-center tw:gap-2 tw:shrink-0">
                                                        <button
                                                            type="button"
                                                            class="tw:inline-flex tw:items-center tw:justify-center tw:shrink-0 tw:w-7 tw:h-7 tw:p-0 tw:m-0 tw:border-2 tw:border-current tw:rounded-full tw:bg-transparent tw:leading-none tw:cursor-pointer tw:select-none tw:transition-colors tw:duration-200 disabled:tw:opacity-35 disabled:tw:cursor-not-allowed hover:enabled:tw:bg-current hover:enabled:[&>span]:tw:invert"
                                                            :disabled="cantidadSolicitada <= 1"
                                                            @click="cantidadSolicitada = Math.max(1, cantidadSolicitada - 1)"
                                                            :aria-label="$t('reservas.restar')"
                                                        ><span class="tw:block tw:leading-none tw:text-base tw:font-normal" aria-hidden="true">−</span></button>

                                                        <b-form-input
                                                            v-model.number="cantidadSolicitada"
                                                            type="number"
                                                            :min="1"
                                                            :max="unitsAvailable"
                                                            class="cantidad-input tw:w-12 tw:h-7 tw:text-center tw:text-base tw:font-bold tw:border-2 tw:border-gray-300 tw:rounded-lg tw:px-1 tw:py-0"
                                                            @change="val => cantidadSolicitada = Math.min(Math.max(1, Number(val)), unitsAvailable)"
                                                        />

                                                        <button
                                                            type="button"
                                                            class="tw:inline-flex tw:items-center tw:justify-center tw:shrink-0 tw:w-7 tw:h-7 tw:p-0 tw:m-0 tw:border-2 tw:border-current tw:rounded-full tw:bg-transparent tw:leading-none tw:cursor-pointer tw:select-none tw:transition-colors tw:duration-200 disabled:tw:opacity-35 disabled:tw:cursor-not-allowed hover:enabled:tw:bg-current hover:enabled:[&>span]:tw:invert"
                                                            :disabled="cantidadSolicitada >= unitsAvailable"
                                                            @click="cantidadSolicitada = Math.min(unitsAvailable, cantidadSolicitada + 1)"
                                                            :aria-label="$t('reservas.sumar')"
                                                        ><span class="tw:block tw:leading-none tw:text-base tw:font-normal" aria-hidden="true">+</span></button>
                                                    </div>
                                                </div>
                                            </template>

                                            <div
                                                v-if="mostrarAvisoComprarCompleta"
                                                class="tw:mt-3 tw:text-sm tw:text-amber-700 tw:bg-amber-50 tw:border tw:border-amber-200 tw:rounded-lg tw:px-4 tw:py-3"
                                                role="alert"
                                            >
                                                {{ $t('reservas.comprar_completa_aviso') }}
                                            </div>

                                            <div class="text-center mt-4">
                                                <button
                                                    type="button"
                                                    class="button effect-button large-button"
                                                    :disabled="!puedeConfirmarCantidad"
                                                    @click="confirmarCantidad(changeStep)"
                                                >
                                                    <span>
                                                        {{ $t('reservas.confirmar_cantidad') }}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template #seleccionZona="{ changeStep }">
                                <div class="container">
                                    <div cols="12" v-if="! zona_seleccionada">
                                        <SeleccionarZonaComponent 
                                            id="selectorZona"
                                            :reserva="reserva"
                                            :zonas_disponibles="zonas_disponibles"
                                            @seleccionarZona="(seats, zona) => { seat_map = seats; zona_seleccionada = zona; changeStep() }"
                                        />
                                    </div>
                                </div>
                            </template>

                            <template #seleccionAsientos="{ changeStep }">
                                <div class="container">
                                    <div cols="12" v-if="! asientos_seleccionados && zona_seleccionada">
                                        <SeleccionarAsientosComponent
                                            :reserva="reserva"
                                            :zona_seleccionada="zona_seleccionada"
                                            :seat_map="seat_map"
                                            :asientos.sync="asientos"
                                            @resetAsientos="resetAsientos"
                                            @asientosSeleccionados="() => { asientos_seleccionados = true; changeStep() }"
                                        />
                                    </div>
                                </div>
                            </template>
            
                            <template #datosCompradores>
                                <div class="container">
                                    <div class="row">
                                        <div class="col-12 pb-5">
                                            <DatosCompradoresComponent
                                                :datos_compradores="datos_compradores" 
                                                :generos="generos" 
                                                :solicitar_datos="reserva.evento.solicitar_datos"
                                                :reserva_instalaciones="reserva.evento.reserva_instalaciones"
                                                :asientos="asientos"
                                                :plazas="entradasPlazas"
                                                :desactivar="resumen"
                                                :lfp="false"
                                                :event="reserva.evento"
                                                @terminar="handleGuardarDatos"
                                                @cambiar-asiento="resetAsientos"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template #resumenCompra="{ changeStep }">
                                <div class="container pb-5">
                                    <div class="row">
                                        <div class="col-12">
                                            <h2 class="m-0 h1 text-uppercase">
                                                {{ $t('partido.resumen_compra') }}
                                            </h2>
                                            <div class="subtitle h3 light mb-3 mb-lg-5">
                                                {{ $t('partido.revisa_datos') }}.
                                            </div>
                                            <ResumenCompraComponent 
                                                :entradas="asientos"
                                                :entradasPlazas="entradasPlazas"
                                                :formulario="formulario"
                                                :event="reserva.evento"
                                                :datos="datos_compradores"
                                                :session_id="session_id"
                                                :cantidad_solicitada="esMultiPrecio ? null : cantidadSolicitada"
                                                :cantidades_por_precio="esMultiPrecio ? cantidadesPorPrecio.filter(p => p.cantidad_solicitada > 0) : null"
                                                :precios_reserva="reserva.precios"
                                                :units_total="unitsTotal"
                                                @terminado="procesarPago"
                                                @volver="() => { asientos_seleccionados = true; changeStep(true); borrarSesion() }"
                                                :desactivar="procesar_pago ? true : false"
                                                :voucher="voucher"
                                            />					
                                        </div>
                                    </div>
                                </div>
                            </template>
            
                            <template #pago>
                                <div class="container">
                                    <div class="row">
                                        <div class="col-12">
                                            <h2 class="m-0 h1 text-uppercase">
                                                {{ $t('compra.confirmacion_compra') }}
                                            </h2>
                                            <div class="subtitle h3 light mb-3 mb-lg-5">
                                                {{ $t('compra.introduce_datos') }}
                                            </div>
                                            <PagoPaycometComponent
                                                ref="pago"
                                                tipo_venta="evento"
                                                :jet_id="jet_id"
                                                :session_id="session_id"
                                            />		
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Stepper>
                    </b-col>
                </b-row>
                <b-row v-if="butacasEnSesion">
                    <b-col cols="12">
                        <PageTitle title="Ha interrumpido el proceso de compra. Por favor, vuelva a intentarlo en 15 minutos."/>
                    </b-col>
                </b-row>
                <b-row v-if="agotado">
                    <b-col cols="12">
                        <PageTitle :title="$t('reservas.agotado')"/>
                    </b-col>
                </b-row>
                <b-row v-if="comprado">
                    <b-col cols="12">
                        <PageTitle title="Ya se han comprado las entradas de este enlace."/>
                    </b-col>
                </b-row>
            </ContentOrLoading>
        </template>
        <b-col v-else>
            <NotFoundComponent/>
        </b-col>
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { getCurrentInstance } from 'vue';
    import { useReservaFlow } from '@/composables/front/reservas/useReservaFlow';

    import PageTitle from '@/components/frontend/shared/PageTitle';
    import NotFoundComponent from '@/components/frontend/errors/NotFoundComponent.vue';
    import CabeceraClubs from '@/components/frontend/events/show/CabeceraClubs.vue';
    import CabeceraImagen from '@/components/frontend/events/show/CabeceraImagen.vue';
    import DatosCompradoresComponent from '@/components/frontend/events/compra/DatosCompradoresComponent.vue';
    import ResumenCompraComponent from '@/components/frontend/events/compra/ResumenCompraComponent';
    import PagoPaycometComponent from '@/components/frontend/compra/components/PagoPaycometComponent.vue';
    import SeleccionarZonaComponent from '@/components/frontend/reservas/SeleccionarZonaComponent.vue';
    import SeleccionarAsientosComponent from '@/components/frontend/reservas/SeleccionarAsientosComponent.vue';
    import Stepper from '@/components/frontend/events/compra/components/Stepper.vue';

    // ── PROPS ─────────────────────────────────────────────────────────────────────
    const props = defineProps({
        hash: { type: String, required: true },
    });

    // ── VUE 2.7 INSTANCE ─────────────────────────────────────────────────────────
    const instance = getCurrentInstance();
    const vm = instance.proxy;

    // ── REF AL STEPPER ────────────────────────────────────────────────────────────
    const stepper = ref(null);

    // ── COMPUTED AUXILIARES ───────────────────────────────────────────────────────
    const generos = vm.$store.getters.getGeneros;
    const configuracion = vm.$store.getters.getConfig;

    // ── COMPOSABLE DE DOMINIO ─────────────────────────────────────────────────────
    const {
        reserva,
        error,
        comprado,
        butacasEnSesion,
        agotado,
        asientos,
        entradasPlazas,
        asientos_seleccionados,
        zona_seleccionada,
        seat_map,
        datos_compradores,
        formulario,
        jet_id,
        session_id,
        resumen,
        procesar_pago,
        voucher,
        steps,
        cantidadSolicitada,
        cantidadesPorPrecio,
        unitsTotal,
        unitsAvailable,
        esGenerico,
        esMultiPrecio,
        puedeConfirmarCantidad,
        mostrarAvisoComprarCompleta,
        zonas_disponibles,
        getCantidadPorPrecio,
        setCantidadPorPrecio,
        incrementarCantidad,
        decrementarCantidad,
        confirmarCantidad,
        guardarDatos,
        borrarSesion,
        procesarPago,
        resetAsientos,
    } = useReservaFlow({
        getVuexAlias: alias => vm.config.getVuexAlias(alias),
        hash: props.hash,
        hashTeatro: configuracion.hash_teatro,
        toast: vm.$toast,
        onStepperChange: back => {
            vm.$nextTick(() => {
                if (stepper.value) stepper.value.changeStep(back);
            });
        },
        onStepperReset: () => {
            if (stepper.value) stepper.value.resetStepper();
        },
        onRouterPush: path => vm.$router.push(path),
        tFn: key => vm.$t(key),
    });

    // ── HANDLER GUARDAR DATOS ─────────────────────────────────────────────────────
    // Puente entre el evento @terminar del componente y el composable,
    // pasando las dependencias de cookies.
    function handleGuardarDatos() {
        guardarDatos(resetAsientos, vm.$cookies);
    }
</script>

<style scoped>
/* Oculta el spinner nativo del input[type=number] — no es reemplazable con clases Tailwind */
.cantidad-input::-webkit-outer-spin-button,
.cantidad-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.cantidad-input {
    -moz-appearance: textfield;
    /*
     * Bootstrap's .form-control applies a fixed calc() height that beats Tailwind
     * tw:h-* utilities. Setting height/padding here — scoped to this component —
     * lets the Tailwind height classes (tw:h-7 in multi-price, tw:h-8 in simple)
     * drive the actual size without Bootstrap interference.
     */
    height: unset !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: normal;
}
</style>
